"use client";

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useRouter } from "next/navigation";
import Fuse from "fuse.js";
import { quizzes } from "~/config/quizzes";
import type { QuizConfig } from "~/types/quiz";
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from "~/components/ui/command";
import { Button } from "~/components/ui/button";
import { Search, ArrowRight, Clock, Users } from "lucide-react";
import { cn } from "~/lib/utils";
import { Badge } from "~/components/ui/badge";

interface QuizSearchProps {
  className?: string;
}

// Define types for Fuse.js results
type FuseResultMatch = {
  indices: readonly [number, number][];
  key?: string;
  refIndex?: number;
  value?: string;
};

type FuseSearchResult = {
  item: QuizConfig;
  refIndex?: number;
  score?: number;
  matches?: readonly FuseResultMatch[];
};

// Helper component to highlight matched text
const HighlightText = ({
  text,
  matches,
}: {
  text: string;
  matches?: readonly FuseResultMatch[];
}) => {
  if (!matches || !matches.length) return <>{text}</>;

  const matchesForKey = matches.filter(
    (match) => match.key === "title" || match.key === "description",
  );
  if (!matchesForKey.length) return <>{text}</>;

  const match = matchesForKey[0];
  if (!match || !match.indices.length) return <>{text}</>;

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  // Sort indices to ensure they're in order
  const sortedIndices = [...match.indices].sort((a, b) => a[0] - b[0]);

  sortedIndices.forEach(([start, end]) => {
    // Add text before the match
    if (start > lastIndex) {
      parts.push(
        <span key={`text-${lastIndex}`}>
          {text.substring(lastIndex, start)}
        </span>,
      );
    }

    // Add the highlighted match
    parts.push(
      <span
        key={`highlight-${start}`}
        className="rounded-sm bg-yellow-100 px-0.5 font-medium"
      >
        {text.substring(start, end + 1)}
      </span>,
    );

    lastIndex = end + 1;
  });

  // Add any remaining text
  if (lastIndex < text.length) {
    parts.push(
      <span key={`text-${lastIndex}`}>{text.substring(lastIndex)}</span>,
    );
  }

  return <>{parts}</>;
};

export function QuizSearch({ className }: QuizSearchProps) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState<FuseSearchResult[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();

  const fuse = useMemo(
    () =>
      new Fuse(quizzes, {
        keys: ["title", "shortTitle", "description"],
        threshold: 0.4,
        includeScore: true,
        includeMatches: true,
      }),
    [],
  );

  const handleSearch = useCallback(
    (value: string) => {
      setSearchTerm(value);

      if (!value.trim()) {
        setResults(quizzes.map((quiz) => ({ item: quiz })));
        return;
      }

      const searchResults = fuse.search(value);
      setResults(searchResults);
    },
    [fuse],
  );

  // Initialize with all quizzes
  useEffect(() => {
    setResults(quizzes.map((quiz) => ({ item: quiz })));
  }, []);

  // Handle keyboard shortcut to open search dialog
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if ((e.key === "k" && (e.metaKey || e.ctrlKey)) || e.key === "/") {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  // Focus input when dialog opens
  useEffect(() => {
    if (open) {
      // Small delay to ensure the dialog is fully rendered
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const handleSelect = (quiz: QuizConfig) => {
    router.push(`/testovi/${quiz.id}`);
    setOpen(false);
  };

  return (
    <>
      <Button
        variant="outline"
        className={cn(
          "relative h-10 w-full justify-start rounded-md bg-background px-4 text-sm font-normal text-muted-foreground shadow-none",
          className,
        )}
        onClick={() => setOpen(true)}
        aria-label="Pretražite testove"
      >
        <Search className="mr-2 h-4 w-4" />
        <span>Pretražite testove...</span>
        <CommandShortcut className="ml-auto">⌘K</CommandShortcut>
      </Button>

      <CommandDialog
        open={open}
        onOpenChange={setOpen}
        title="Pretraga testova"
      >
        <Command className="rounded-lg border shadow-md">
          <CommandInput
            ref={inputRef}
            placeholder="Pretražite testove..."
            value={searchTerm}
            onValueChange={handleSearch}
            className="border-none focus:ring-0"
          />
          <CommandList className="max-h-[300px] overflow-auto">
            <CommandEmpty className="py-6 text-center text-sm">
              Nema rezultata za "
              <span className="font-medium">{searchTerm}</span>"
            </CommandEmpty>
            <CommandGroup heading="Testovi" className="px-2">
              {results.map((result) => {
                const quiz = result.item;
                return (
                  <CommandItem
                    key={quiz.id}
                    onSelect={() => handleSelect(quiz)}
                    className="flex flex-col items-start rounded-md px-2 py-3 aria-selected:bg-accent/50"
                  >
                    <div className="flex w-full items-center">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
                        <quiz.icon className="h-4 w-4 text-brand" />
                      </div>
                      <div className="ml-2 flex-1">
                        <div className="font-medium">
                          {searchTerm ? (
                            <HighlightText
                              text={quiz.title}
                              matches={result.matches?.filter(
                                (m) => m.key === "title",
                              )}
                            />
                          ) : (
                            quiz.title
                          )}
                        </div>
                        <div className="line-clamp-1 text-xs text-muted-foreground">
                          {searchTerm ? (
                            <HighlightText
                              text={quiz.description}
                              matches={result.matches?.filter(
                                (m) => m.key === "description",
                              )}
                            />
                          ) : (
                            quiz.description
                          )}
                        </div>
                      </div>
                      <ArrowRight className="ml-2 h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="mt-2 flex w-full items-center gap-2">
                      <Badge
                        variant="secondary"
                        className="flex items-center gap-1"
                      >
                        <Clock className="h-3 w-3" />
                        <span>{quiz.duration}</span>
                      </Badge>
                      <Badge
                        variant="secondary"
                        className="flex items-center gap-1"
                      >
                        <Users className="h-3 w-3" />
                        <span>{quiz.completions ?? 0}+ ljudi</span>
                      </Badge>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </CommandDialog>
    </>
  );
}
